/**
 * tapp's appback adapter for the vendored Live Tap renderer's `resources`
 * contract: symbols ({ loadMasterSymbols(), loadSymbols(),
 * loadSymbolImages(symbolId) }) plus the curated layout/background catalog
 * ({ loadPrimarySymbols(), loadBackgrounds(), loadLayouts(),
 * loadBackgroundDetail(id) }). A symbol is referenced by its `symbol_id`
 * everywhere (TT-1159); its `name` is only shown.
 *
 * appback returns camelCase (it auto-converts snake_case -> camelCase) but the
 * conversion is shallow (top-level keys only), while the vendored renderer
 * expects snake_case for symbol fields (symbol_id, image_file, height_ratio)
 * and for the authored slot internals (slot.textpath_data, font_size, ...).
 * This adapter bridges both: it maps top-level fields to the renderer's shape
 * and passes slot arrays through untouched (their snake_case internals survive
 * appback's shallow conversion). The vendored tree is never hand-edited.
 *
 * appback's layout API differs from tapsrv's: `getLayouts()` returns layouts
 * with their backgrounds nested and, asked with `includeSlots` (TT-1265), each
 * layout's slots too, so the catalog is one call. `getLayout(id)` returns a
 * single layout's slots: the fallback for a layout the list carried no
 * `slots` array for (an appback that predates `?include=slots`, or one that
 * left them out for size), so a deploy-order mismatch can't blank the
 * catalog. The background detail reads the listing the same way.
 * Layouts/backgrounds don't change within a session, so the fetches are
 * cached on the adapter instance.
 *
 * This file is tapp-owned and lives outside src/vendor/ on purpose: resource
 * adapters are per-front-end and are not vendored.
 *
 * `api` is where the catalog calls go: AppBackService (a signed-in host)
 * or a live-link api (liveTap/liveLinkResourceApi.js — a public viewer,
 * TT-1110). Same call names, same response shapes.
 */

import AppBackService from "../services/appback";
// Background image URLs are built by the raw-S3 convention (NOT appback's
// image_url, which since TT-744 points at the CloudFront CDN that blanks
// backgrounds). Shared with the modal/DetailPage so there's one source of truth.
import { backgroundImageUrl, backgroundPieceUrl } from "../services/utilities";

// Normalize the backend `pieces` array (ordered [{ seq }, ...]; appback's shallow
// camelCasing leaves the nested objects' `seq` untouched) into ordered reveal
// entries with derived URLs. Returns [] when a background has no pieces.
function normalizePieces(id, pieces) {
  if (!Array.isArray(pieces)) return [];
  return pieces
    .map((p) => {
      const seq = typeof p === "number" ? p : p?.seq;
      const url = backgroundPieceUrl(id, seq);
      return url ? { seq, url } : null;
    })
    .filter(Boolean);
}

function numOrNull(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// A master symbol is nested in its response, so it keeps appback's
// snake_case: `{ symbol_id, name, tags, fg, primary, … }` as listed.
function toMasterSymbol(entry = {}) {
  return {
    ...entry,
    fg: entry.fg || {},
    tags: entry.tags || [],
  };
}

function toSymbolImage(entry = {}) {
  return {
    image_file: entry.imageFile ?? entry.image_file ?? entry.svg,
    height_ratio: entry.heightRatio ?? entry.height_ratio ?? 1,
    selected: Boolean(entry.selected),
    styles: entry.styles || [],
  };
}

// A background nested under a layout (getLayouts) -> the renderer's catalog
// shape. layoutId comes from the parent layout (authoritative). getLayouts
// already returns only published backgrounds; `published` is the one flag
// (TT-968), and the renderer treats an absent field as published.
function toCatalogBackground(bg = {}, layoutId) {
  const id = bg.id ?? bg._id ?? "";
  return {
    id,
    layoutId,
    primarySymbolId: bg.primarySymbolId ?? "",
    symbolIds: bg.symbolIds || [],
    styles: bg.styles || [],
    colors: bg.colors || {},
    font: bg.font || null,
    heightRatio: numOrNull(bg.heightRatio ?? bg.height_ratio),
    imageUrl: backgroundImageUrl(id),
    // Whether this is a progressive multi-piece background (reveals one piece at a
    // time) vs. a single image. Used by the picker to surface piece backgrounds
    // first; the full ordered pieces come from the detail fetch.
    hasPieces: Array.isArray(bg.pieces) && bg.pieces.length > 0,
  };
}

// Slots as the renderer takes them: an array, whatever a detail call returned.
function slotsOf(detail) {
  return Array.isArray(detail?.slots) ? detail.slots : [];
}

export function createAppbackResourceAdapter(api = AppBackService) {
  // Session-scoped caches: layouts/backgrounds are static within a tap, and the
  // three catalog methods share these fetches.
  let layoutsPromise = null; // getLayouts() — layouts with nested backgrounds (+ slots)
  const layoutSlotsCache = new Map(); // layoutId -> getLayout() (slots/title)
  const descriptionCache = new Map(); // backgroundId -> description string
  const artCache = new Map(); // renderer ("board" | "scene") -> its art manifest
  let symbolsPromise = null; // getSymbols() — the catalog's symbols, as a list

  function fetchLayouts() {
    if (!layoutsPromise) {
      layoutsPromise = api.getLayouts({ includeSlots: true }).catch((err) => {
        layoutsPromise = null; // let a later call retry
        throw err;
      });
    }
    return layoutsPromise;
  }

  function fetchLayoutSlots(layoutId) {
    if (!layoutId) return Promise.resolve(null);
    if (!layoutSlotsCache.has(layoutId)) {
      layoutSlotsCache.set(
        layoutId,
        api.getLayout(layoutId).catch(() => null)
      );
    }
    return layoutSlotsCache.get(layoutId);
  }

  async function activeLayouts() {
    const data = await fetchLayouts();
    return (data?.layouts || []).filter((l) => l && l.active);
  }

  // A listed layout's slots/title/name: the listing itself when appback sent
  // a `slots` array (`?include=slots`, TT-1265), else that layout's own
  // getLayout, the pre-TT-1265 path. The fallback means an appback that
  // predates the option (deploy order), or one that dropped the slots for
  // size; it is said once per adapter (session), since it is the call burst
  // TT-1265 removed.
  let fallbackWarned = false;
  function layoutDetail(layout) {
    if (Array.isArray(layout.slots)) return Promise.resolve(layout);
    if (!fallbackWarned) {
      fallbackWarned = true;
      console.warn(
        "Live Tap catalog: /tap/layouts?include=slots came back without slots; " +
          "fetching each layout's slots with /tap/layouts/{id} instead"
      );
    }
    return fetchLayoutSlots(layout.id);
  }

  return {
    async loadMasterSymbols() {
      const data = await api.getMasterSymbols();
      const list = Array.isArray(data) ? data : data?.symbols || [];
      const domains = (Array.isArray(data) ? [] : data?.domains) || [];
      return { symbols: list.map(toMasterSymbol), domains };
    },

    // Every symbol in the catalog, [{ symbol_id, name, … }]: what the
    // canvas's read-outs and the host's (useSymbolName) show for a symbol is
    // its name from here. Read once per adapter and shared by every asker; a
    // failed read is not kept, so the next asker retries. The service answers
    // a failure with an `{ error, … }` object, not a throw: it lists no symbol.
    loadSymbols() {
      if (!symbolsPromise) {
        symbolsPromise = Promise.resolve()
          .then(() => api.getSymbols())
          .then((bySymbolId) => {
            const symbols = Object.values(bySymbolId || {}).filter((s) => s?.symbol_id);
            if (!symbols.length) throw new Error("appback served no symbol catalog");
            return symbols;
          })
          .catch((err) => {
            symbolsPromise = null;
            throw err;
          });
      }
      return symbolsPromise;
    },

    // Curated primary-symbol whitelist (TT-936): the symbol_ids allowed to
    // become the rendered primary and drive background selection. Derived from
    // the master-symbols response (every master symbol carries a `primary`
    // flag), which a live link serves too; the few background-only primary
    // themes that aren't master symbols never appear as foreground here, so
    // they don't affect gating.
    async loadPrimarySymbols() {
      try {
        const data = await api.getMasterSymbols();
        const list = Array.isArray(data) ? data : data?.symbols || [];
        return list
          .filter((s) => s && s.primary)
          .map((s) => s.symbol_id)
          .filter(Boolean);
      } catch {
        return [];
      }
    },

    async loadSymbolImages(symbolId) {
      const docs = await api.getSymbolImages(symbolId);
      return (Array.isArray(docs) ? docs : []).map(toSymbolImage);
    },

    async loadBackgrounds() {
      try {
        const layouts = await activeLayouts();
        const out = [];
        for (const layout of layouts) {
          for (const bg of layout.backgrounds || []) out.push(toCatalogBackground(bg, layout.id));
        }
        return out;
      } catch {
        return [];
      }
    },

    async loadLayouts() {
      try {
        // Only active layouts with backgrounds matter; each needs its slots so
        // the catalog can exclude slotless layouts (see layoutDetail).
        const layouts = (await activeLayouts()).filter((l) => (l.backgrounds || []).length);
        return Promise.all(
          layouts.map(async (l) => {
            const detail = await layoutDetail(l);
            return {
              id: l.id,
              name: l.name ?? "",
              slots: slotsOf(detail),
              title: detail?.title || null,
              active: true,
            };
          })
        );
      } catch {
        return [];
      }
    },

    async loadBackgroundDetail(id) {
      const key = (id || "").trim();
      if (!key) return null;
      try {
        const layouts = await activeLayouts();
        let bg = null;
        let owner = null;
        for (const layout of layouts) {
          const found = (layout.backgrounds || []).find((b) => (b.id ?? b._id) === key);
          if (found) {
            bg = found;
            owner = layout;
            break;
          }
        }
        if (!bg) return null;
        const detail = await layoutDetail(owner);
        return {
          id: key,
          colors: bg.colors || {},
          // h/w ratio of the background image — defines the layout coordinate
          // space (viewBox 1800 × 1800*heightRatio) the slots were authored in.
          heightRatio: numOrNull(bg.heightRatio ?? bg.height_ratio),
          font: bg.font || null,
          primarySymbolId: bg.primarySymbolId ?? "",
          symbolIds: bg.symbolIds || [],
          styles: bg.styles || [],
          imageUrl: backgroundImageUrl(key),
          // Ordered progressive-reveal pieces; empty ⇒ single-image reveal.
          pieces: normalizePieces(key, bg.pieces),
          layout: {
            id: owner.id,
            name: detail?.name ?? "",
            // each slot carries its merged `rules` array; snake_case internals
            // (textpath_data, max_size, ...) survive appback's shallow casing.
            slots: slotsOf(detail),
            title: detail?.title || null,
          },
        };
      } catch {
        return null;
      }
    },

    // Host-only (TT-972) — not part of the renderer's `resources` contract; the
    // renderer never calls it. Field-guide description of a background's primary
    // symbol, as the draft-js raw JSON string taptap authors it in ("" when the
    // background has none). Same endpoint the detail page's transcript panel reads.
    // Cached per background like the layout fetches above: a description never
    // changes within a session, and the renderer returns to earlier backgrounds
    // freely (a manual pin, or hysteresis settling back). appback goes through
    // axios with no HTTP cache, so an uncached repeat is a real round trip.
    loadBackgroundDescription(id) {
      const key = (id || "").trim();
      if (!key) return Promise.resolve("");
      if (!descriptionCache.has(key)) {
        descriptionCache.set(
          key,
          api.getBackgroundDescription(key)
            .then((resp) => resp?.description || "")
            .catch(() => {
              descriptionCache.delete(key); // let a later open retry
              return "";
            })
        );
      }
      return descriptionCache.get(key);
    },

    // The board or scene renderer's art manifest from the catalog (TT-1310):
    // the object, or null when appback has none to give (one that does not
    // serve it yet, or a refused read). Cached per renderer for the session,
    // like the layouts; a failure is not cached, and is said.
    loadArtManifest(renderer) {
      if (!artCache.has(renderer)) {
        artCache.set(
          renderer,
          Promise.resolve()
            .then(() => api.getArtManifest(renderer))
            .then((m) => (m && typeof m.assets === "object" ? m : null))
            .catch(() => null)
            .then((m) => {
              if (!m) {
                artCache.delete(renderer);
                console.warn(`Live Tap art: appback served no ${renderer} art manifest; drawing without art`);
              }
              return m;
            })
        );
      }
      return artCache.get(renderer);
    },
  };
}
